import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../_theme/palette.js';
import { hexWithAlpha } from '../../../_theme/palette.js';
import type { SocialLink, SocialPlatform } from '../types.js';
import { SocialIcon } from './SocialIcons.js';

interface SocialLinksRowProps {
  palette: Palette;
  links: SocialLink[];
}

const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  facebook: 'Facebook',
  twitter: 'X',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  github: 'GitHub',
  discord: 'Discord',
  website: 'Website',
  custom: 'Link',
};

export function SocialLinksRow({ palette, links }: SocialLinksRowProps): JSX.Element | null {
  if (!links?.length) return null;
  return (
    <div style={rowStyle()}>
      {links.map((link, index) => (
        <SocialPill key={`${link.platform}-${index}`} link={link} palette={palette} />
      ))}
    </div>
  );
}

function SocialPill({ link, palette }: { link: SocialLink; palette: Palette }): JSX.Element {
  const label = link.label?.trim() || PLATFORM_LABELS[link.platform] || 'Link';
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" style={pillStyle(palette)}>
      <span style={{ color: palette.textMuted, display: 'inline-flex' }}>
        <SocialIcon platform={link.platform} size={16} />
      </span>
      <span>{label}</span>
    </a>
  );
}

function rowStyle(): CSSProperties {
  return {
    marginTop: 24,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
  };
}

function pillStyle(palette: Palette): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${hexWithAlpha(palette.textMuted, 0.2)}`,
    background: palette.card,
    color: palette.textMuted,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    lineHeight: 1.2,
  };
}
